import { useState, useEffect, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Button, Card, Badge } from '@/components/ui';
import type { CargoItem, ItineraryItem } from '@/types/cargoList.types';
import { SortableItineraryItem } from './SortableItineraryItem';
import {
  formatDateRange,
  formatCargoSize,
  formatCommission,
  getPrivacyLevelBadgeVariant,
  getStatusBadgeVariant,
} from '@/utils/cargoListUtils';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  Package,
  Users,
  Eye,
  Ship,
  Clock,
  TrendingUp,
  FileText,
  DollarSign,
  Route,
  Plus,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface CargoDetailsViewProps {
  cargo: CargoItem;
  itinerary: ItineraryItem[];
  onBack: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAddItinerary?: () => void;
  onEditItinerary?: (item: ItineraryItem) => void;
  onReorderItinerary?: (oldOrder: number, newOrder: number) => Promise<void>;
}

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Phone-width form - the weekday and year are the first things to drop. */
const formatDateTimeShort = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Full timestamp on a real screen, trimmed one so a phone row stays on one line. */
function Timestamp({ value }: { value: string }) {
  return (
    <>
      <span className="sm:hidden">{formatDateTimeShort(value)}</span>
      <span className="hidden sm:inline">{formatDateTime(value)}</span>
    </>
  );
}

/**
 * One of the four headline facts. A phone gets a compact two-up card - small
 * icon inline with the label, value on its own full-width line - rather than the
 * centred tile, which ran one per row and pushed the actual detail two screens
 * down.
 */
function KeyFactCard({
  icon,
  iconWrapperClassName,
  label,
  children,
}: {
  icon: ReactNode;
  iconWrapperClassName: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <Card noPadding>
      <div className="flex flex-col gap-1 p-3 sm:items-center sm:gap-3 sm:p-6 sm:text-center">
        <div className="flex items-center gap-2 sm:flex-col sm:gap-3">
          <div
            className={clsx(
              'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12',
              iconWrapperClassName
            )}
          >
            {icon}
          </div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:text-sm">{label}</p>
        </div>
        {children}
      </div>
    </Card>
  );
}

/** Titled card body. Padding and heading shrink a step below `sm`. */
function SectionCard({
  icon,
  title,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card noPadding>
      <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700 sm:pb-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {icon}
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
              {title}
            </h3>
          </div>
          {action}
        </div>
        {children}
      </div>
    </Card>
  );
}

/**
 * Label/value pair. The value is right-aligned and allowed to wrap rather than
 * shoving its label off a narrow screen.
 */
function DetailRow({
  label,
  children,
  valueClassName = 'break-words text-sm font-semibold text-gray-900 dark:text-white',
}: {
  label: string;
  children: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className={clsx('min-w-0 text-right', valueClassName)}>{children}</span>
    </div>
  );
}

export function CargoDetailsView({
  cargo,
  itinerary,
  onBack,
  onEdit,
  onDelete,
  onAddItinerary,
  onEditItinerary,
  onReorderItinerary,
}: CargoDetailsViewProps) {
  // Local state for optimistic updates
  const [displayItinerary, setDisplayItinerary] = useState<ItineraryItem[]>(itinerary);

  // Sync display itinerary with prop changes (when data loads or refreshes)
  useEffect(() => {
    setDisplayItinerary(itinerary);
  }, [itinerary]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const sortedItinerary = [...displayItinerary].sort((a, b) => a.order - b.order);
    const oldIndex = sortedItinerary.findIndex((item) => item.order.toString() === active.id);
    const newIndex = sortedItinerary.findIndex((item) => item.order.toString() === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Capture the actual server-side order numbers BEFORE renumbering — the API
    // expects the original order ids, not array positions (which only match by
    // coincidence when items are densely numbered 1..N).
    const movedOrder = sortedItinerary[oldIndex].order;
    const targetOrder = sortedItinerary[newIndex].order;

    const originalItems = displayItinerary;

    // Optimistic local renumber so the UI doesn't flicker before the refetch.
    const reorderedItems = arrayMove(sortedItinerary, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    setDisplayItinerary(reorderedItems);

    try {
      await onReorderItinerary?.(movedOrder, targetOrder);
    } catch (error) {
      setDisplayItinerary(originalItems);
    }
  };

  return (
    <div className="space-y-5 sm:space-y-8">
      {/* Header Section - Clean and Simple */}
      <div className="flex flex-col gap-3 sm:gap-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-3 self-start"
        >
          Back to Cargo List
        </Button>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 space-y-1.5 sm:space-y-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <Ship className="h-5 w-5 flex-shrink-0 text-primary-600 sm:h-6 sm:w-6" />
              <h1 className="min-w-0 truncate text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
                {cargo.charterer}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400 sm:gap-x-4 sm:text-sm">
              <span>ID: {cargo.id}</span>
              <span aria-hidden="true">•</span>
              <span>{cargo.trade}</span>
              <span aria-hidden="true">•</span>
              <Badge variant={getStatusBadgeVariant(cargo.status)} size="sm">
                {cargo.status}
              </Badge>
            </div>
          </div>

          {/* Full-width pair on a phone, so both stay thumb-sized without a
              button row as tall as the title above it. */}
          {(onEdit || onDelete) && (
            <div className="flex gap-2 sm:flex-shrink-0 sm:gap-3">
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEdit}
                  leftIcon={<Edit className="h-4 w-4" />}
                  className="flex-1 sm:flex-none"
                >
                  Edit
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDelete}
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  className="flex-1 sm:flex-none"
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Key Information Cards - two-up on a phone, one row from `lg` */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KeyFactCard
          label="Cargo Size"
          iconWrapperClassName="bg-blue-100 dark:bg-blue-900/50"
          icon={<Package className="h-4 w-4 text-blue-600 dark:text-blue-400 sm:h-6 sm:w-6" />}
        >
          <p className="text-lg font-bold leading-tight text-gray-900 dark:text-white sm:text-2xl">
            {formatCargoSize(cargo.cargoSize)}
          </p>
        </KeyFactCard>

        <KeyFactCard
          label="Laycan Period"
          iconWrapperClassName="bg-green-100 dark:bg-green-900/50"
          icon={<Calendar className="h-4 w-4 text-green-600 dark:text-green-400 sm:h-6 sm:w-6" />}
        >
          <p className="text-sm font-bold leading-tight text-gray-900 dark:text-white sm:text-lg">
            {formatDateRange(cargo.lcStart, cargo.lcEnd)}
          </p>
        </KeyFactCard>

        <KeyFactCard
          label="Privacy Level"
          iconWrapperClassName="bg-yellow-100 dark:bg-yellow-900/50"
          icon={<Eye className="h-4 w-4 text-yellow-600 dark:text-yellow-400 sm:h-6 sm:w-6" />}
        >
          <div>
            <Badge variant={getPrivacyLevelBadgeVariant(cargo.privacyLevel)} size="md">
              {cargo.privacyLevel}
            </Badge>
          </div>
        </KeyFactCard>

        <KeyFactCard
          label="Certainty"
          iconWrapperClassName="bg-purple-100 dark:bg-purple-900/50"
          icon={
            <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400 sm:h-6 sm:w-6" />
          }
        >
          <p className="text-lg font-bold leading-tight text-gray-900 dark:text-white sm:text-2xl">
            {cargo.certainty}
          </p>
        </KeyFactCard>
      </div>

      {/* Main Information - Organized by Purpose */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Cargo Details */}
        <SectionCard
          title="Cargo Details"
          icon={<Package className="h-5 w-5 flex-shrink-0 text-primary-600" />}
        >
          <div className="space-y-3 sm:space-y-4">
            <DetailRow label="Type:">{cargo.type}</DetailRow>
            <DetailRow label="Trade:">{cargo.trade}</DetailRow>
            <DetailRow label="Laytime:">{cargo.laytime} hours</DetailRow>
            <DetailRow label="Max Age:">{cargo.maxAge || 'Not specified'}</DetailRow>
            <DetailRow label="Fixed Direct:">
              <Badge variant={cargo.isFixedDirect ? 'success' : 'default'} size="sm">
                {cargo.isFixedDirect ? 'Yes' : 'No'}
              </Badge>
            </DetailRow>
          </div>
        </SectionCard>

        {/* Timeline & Status */}
        <SectionCard
          title="Timeline & Status"
          icon={<Clock className="h-5 w-5 flex-shrink-0 text-primary-600" />}
        >
          <div className="space-y-3 sm:space-y-4">
            <DetailRow label="Reported:">
              <Timestamp value={cargo.reportedTime} />
            </DetailRow>
            <DetailRow label="Last Updated:">
              <Timestamp value={cargo.updatedTime} />
            </DetailRow>
            <DetailRow label="Updated By:">{cargo.updatedBy}</DetailRow>
            <DetailRow label="Daily Report:">
              <Badge variant={cargo.isOnDailyReport ? 'success' : 'default'} size="sm">
                {cargo.isOnDailyReport ? 'Included' : 'Not Included'}
              </Badge>
            </DetailRow>
          </div>
        </SectionCard>

        {/* Financial Information */}
        <SectionCard
          title="Commission"
          icon={<DollarSign className="h-5 w-5 flex-shrink-0 text-primary-600" />}
        >
          <div className="space-y-3 sm:space-y-4">
            <DetailRow
              label="Primary Commission:"
              valueClassName="text-base font-bold text-green-600 dark:text-green-400 sm:text-lg"
            >
              {formatCommission(cargo.primaryComm)}
            </DetailRow>
            <DetailRow
              label="Address Commission:"
              valueClassName="text-base font-bold text-blue-600 dark:text-blue-400 sm:text-lg"
            >
              {formatCommission(cargo.addressComm)}
            </DetailRow>
          </div>
        </SectionCard>

        {/* Business Partners */}
        <SectionCard
          title="Business Partners"
          icon={<Users className="h-5 w-5 flex-shrink-0 text-primary-600" />}
        >
          <div className="space-y-3 sm:space-y-4">
            <DetailRow label="Primary Broker:">{cargo.primaryBroker || 'Not specified'}</DetailRow>
            <DetailRow label="Rate Assessor:">{cargo.rateAssessor || 'Not specified'}</DetailRow>
            <DetailRow label="Source:">{cargo.source || 'Not specified'}</DetailRow>
            <DetailRow label="Broker Shared:">
              <Badge variant={cargo.brokerDidNotShare ? 'error' : 'success'} size="sm">
                {cargo.brokerDidNotShare ? 'No' : 'Yes'}
              </Badge>
            </DetailRow>
          </div>
        </SectionCard>
      </div>

      {/* Notes Section */}
      {cargo.note && (
        <SectionCard
          title="Notes"
          icon={<FileText className="h-5 w-5 flex-shrink-0 text-primary-600" />}
        >
          <p className="text-sm text-gray-700 dark:text-gray-300 sm:text-base">{cargo.note}</p>
        </SectionCard>
      )}

      {/* Voyage Itinerary - Clean Timeline Design */}
      <SectionCard
        title="Voyage Itinerary"
        icon={<Route className="h-5 w-5 flex-shrink-0 text-primary-600" />}
        action={
          onAddItinerary && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddItinerary}
              leftIcon={<Plus className="h-4 w-4" />}
              className="flex-shrink-0"
            >
              Add Port
            </Button>
          )
        }
      >
        {displayItinerary.length > 0 ? (
          (() => {
            const sortedItems = [...displayItinerary].sort((a, b) => a.order - b.order);
            return (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortedItems.map((item) => item.order.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3 sm:space-y-4">
                    {sortedItems.map((port, index) => (
                      <SortableItineraryItem
                        key={port.order}
                        item={port}
                        index={index}
                        totalItems={sortedItems.length}
                        onEdit={onEditItinerary}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            );
          })()
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center dark:border-gray-700 sm:p-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 sm:h-16 sm:w-16">
              <Ship className="h-6 w-6 text-gray-400 sm:h-8 sm:w-8" />
            </div>
            <h4 className="mt-3 text-base font-medium text-gray-900 dark:text-white sm:mt-4 sm:text-lg">
              No Voyage Itinerary
            </h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:mt-2">
              The itinerary for this cargo has not been defined yet.
            </p>
            {onAddItinerary && (
              <Button
                variant="primary"
                size="sm"
                onClick={onAddItinerary}
                leftIcon={<Plus className="h-4 w-4" />}
                className="mt-4"
              >
                Add First Port
              </Button>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
